import config from 'app/config'
import { acknowledgeUpgradeModalMutation, acknowledgeUpgradeModalResult } from 'app/mutations/member/acknowledge-upgrade-modal'
import { ALL_ACCESS_TIER } from 'app/constants/tiers'
import makeSiteGraphqlRequest from 'app/lib/graphql-request'
import { Instance, SnapshotOut, types, getRoot } from 'mobx-state-tree'

import { AuthModel, AuthSnapshot } from '../auth'
import { withEnvironment } from '../extensions/with-config'
import { UserModel, UserSnapshot } from '../user'
import refreshAccessTokenImpl from './lib/access-token-refresher'
import retrieveAccessTokenAndUser from './lib/access-token-fetcher'
import generateLoginUrl from './lib/login-link-generator'
import createRetrieveParams from './lib/retrieve-param-creator'
import {
  accessTokenExpired,
  refreshTokenExpired,
} from './lib/token-expiry-determiner'
import { isWeb } from '@mythical/ui'

type UpgradeModalRoot = {
  upgradeModalState: {
    lastCelebratedUpgradeKey: string
    queueCelebrationModal: (event: { key: string; needsAck?: boolean }) => void
  }
}

const normalizeTier = (tier?: string | null) => tier?.trim().toLowerCase()
const isAllAccessTier = (tier?: string | null) =>
  normalizeTier(tier) === normalizeTier(ALL_ACCESS_TIER.name)

const buildUpgradeEventKey = (user: UserSnapshot) => {
  const userId = user.id || 'unknown-user'
  const tier = normalizeTier(user.tier) || 'unknown-tier'
  const activatedAt = user.activatedAt || 'unknown-activation'

  return `${userId}:${tier}:${activatedAt}`
}

export const UserStoreModel = types
  .model('UserStore')
  .props({
    user: types.maybe(UserModel),
    auth: types.maybe(AuthModel),
    challenge: types.maybe(types.string),
    loginUrl: types.maybe(types.string),
    returnPath: types.maybe(types.string),
    loading: types.optional(types.boolean, false),
    canMemberfulLink: types.optional(types.boolean, isWeb ? true : false),
    isAuthInProgress: types.optional(types.boolean, false),
  })
  .extend(withEnvironment)
  .actions((self) => ({
    setLoading: (loading: boolean) => {
      self.loading = loading
    },
    setUser(user?: UserSnapshot) {
      self.user = user
    },
    setSyncedUser(user?: UserSnapshot) {
      const previousUser = self.user
      self.user = user

      if (!user?.id) return

      const hasBackendUpgradeFlag = !!user.showUpgradeModal
      const isSameUser = !!previousUser?.id && previousUser.id === user.id
      const hasTierTransitionToAllAccess =
        isSameUser &&
        !isAllAccessTier(previousUser?.tier) &&
        isAllAccessTier(user.tier)

      if (!hasBackendUpgradeFlag && !hasTierTransitionToAllAccess) {
        return
      }

      let rootStore: UpgradeModalRoot
      try {
        rootStore = getRoot(self) as UpgradeModalRoot
      } catch {
        return
      }
      const upgradeEventKey = buildUpgradeEventKey(user)

      if (
        rootStore.upgradeModalState.lastCelebratedUpgradeKey ===
        upgradeEventKey
      ) {
        if (hasBackendUpgradeFlag && self.user) self.user.showUpgradeModal = false
        return
      }

      rootStore.upgradeModalState.queueCelebrationModal({
        key: upgradeEventKey,
        needsAck: hasBackendUpgradeFlag,
      })
    },
    setChallenge(challenge: string) {
      self.challenge = challenge
    },
    setLoginUrl: (url: string) => {
      self.loginUrl = url
    },
    setReturnPath: (path?: string) => {
      self.returnPath = path
    },
    setCanMemberfulLink: (value: boolean) => {
      self.canMemberfulLink = value
    },
    setAuthInProgress: (value: boolean) => {
      self.isAuthInProgress = value
    },
    acknowledgeUpgradeCelebration: async () => {
      if (!self.user?.id || !self.user.showUpgradeModal) return

      try {
        await makeSiteGraphqlRequest(
          self.environment.config || config,
          acknowledgeUpgradeModalMutation(),
          {},
          {
            userStore: self as UserStore,
            formatFn: acknowledgeUpgradeModalResult,
          }
        )
      } catch (error) {
        console.error(
          '[UserStore] acknowledgeUpgradeCelebration() error:',
          error
        )
      } finally {
        if (self.user) {
          self.user.showUpgradeModal = false
        }
      }
    },
    updateAuth(auth: Partial<AuthSnapshot>) {
      const {
        accessToken,
        refreshToken,
        accessTokenExpiry,
        refreshTokenExpiry,
      } = auth || {}

      const updatedState: Partial<AuthSnapshot> = {
        accessToken,
        refreshToken,
        accessTokenExpiry,
      }

      if (refreshTokenExpiry) {
        updatedState.refreshTokenExpiry = refreshTokenExpiry
      }

      self.auth = Object.assign({}, self.auth, updatedState)
    },
    logout() {
      self.user = undefined
      self.auth = undefined
      self.challenge = undefined
      self.loginUrl = undefined
    },
  }))
  .actions((self) => ({
    generateLoginUrl: (returnPath?: string) => {
      // On web, use SSA flow via /api/auth/login
      if (typeof window !== 'undefined' && !('ReactNativeWebView' in window) && window.location) {
        const path = returnPath || window.location.pathname || '/'
        return `/api/auth/login?returnPath=${encodeURIComponent(path)}`
      }

      // On native, use PKCE flow - return cached URL if available
      if (self.challenge && self.loginUrl) {
        return self.loginUrl
      }

      // Generate URL but DON'T store it here - this function can be called during render
      // The URL will be regenerated each time until initLoginUrl() is called
      const { url } = generateLoginUrl(
        self.environment.config.memberfulOauthUrl,
        self.environment.config.clientId
      )

      return url
    },
    // Call this to initialize and cache the login URL (should be called from useEffect, not render)
    initLoginUrl: () => {
      if (self.challenge && self.loginUrl) {
        return self.loginUrl
      }

      const { url, challenge } = generateLoginUrl(
        self.environment.config.memberfulOauthUrl,
        self.environment.config.clientId
      )

      self.setChallenge(challenge)
      self.setLoginUrl(url)

      return url
    },
    completeLogin: async (code: string) => {
      self.setLoading(true)
      const conf = self.environment.config || config

      const params = createRetrieveParams(conf, self as UserStore, code)

      try {
        const { auth, user } = await retrieveAccessTokenAndUser(
          self as UserStore,
          conf,
          params
        )

        self.updateAuth(auth)
        self.setSyncedUser(user)
        self.setLoading(false)
      } catch (error) {
        console.error('[UserStore] completeLogin() error getting token:', error)
        self.logout()
      } finally {
        self.setAuthInProgress(false)
      }

      return self.user
    },
    refreshAccessToken: async () => {
      self.setLoading(true)
      const store = self as UserStore
      const conf = self.environment.config || config

      if (accessTokenExpired(store) && refreshTokenExpired(store)) {
        self.logout()
        self.setLoading(false)
        return
      }

      if (!self.auth?.accessToken || !self.auth?.refreshToken) {
        self.logout()
        self.setLoading(false)
        return
      }

      try {
        const result = await refreshAccessTokenImpl(store, conf)

        if (!result) {
          self.logout()
          self.setLoading(false)
          return
        }

        const { auth, user } = result

        self.updateAuth(auth)
        self.setSyncedUser(user)
        self.setLoading(false)

        return { auth, user } as { auth: AuthSnapshot; user: UserSnapshot }
      } catch (error) {
        console.error('[UserStore] refreshAccessToken() error:', error)
        self.logout()
        self.setLoading(false)
        return
      }
    },
  }))

type UserStoreType = Instance<typeof UserStoreModel>
export interface UserStore extends UserStoreType {}
type UserStoreSnapshotType = SnapshotOut<typeof UserStoreModel>
export interface UserStoreSnapshot extends UserStoreSnapshotType {}
export const createUserStoreDefaultModel = () =>
  types.optional(UserStoreModel, {})
